// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  ThemeContext,
  DEFAULT_THEME_STYLE,
  type Theme,
  type ThemeStyle,
  type TextureMode,
  type FontMode,
  type ThemeContextValue,
} from '@/contexts/theme';
import type { PerformanceMode } from '@/types';

const PERFORMANCE_MODE_STORAGE_KEY = 'performance-mode';
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
const THEME_STYLE_STORAGE_KEY = 'theme-style';
const TEXTURE_MODE_STORAGE_KEY = 'theme-texture';
const FONT_MODE_STORAGE_KEY = 'theme-font';

function resolveStoredValue<T>(key: string, valid: T[], fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null && (valid as string[]).includes(raw)) return raw as T;
  } catch {
    // ignore
  }
  return fallback;
}

function resolvePerformanceMode(): PerformanceMode {
  if (window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches) {
    return 'reduced-motion';
  }

  const storedMode = window.localStorage.getItem(PERFORMANCE_MODE_STORAGE_KEY);
  if (
    storedMode === 'balanced' ||
    storedMode === 'high-fidelity' ||
    storedMode === 'reduced-motion'
  ) {
    return storedMode;
  }

  return 'balanced';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem('theme') as Theme) || 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  const [themeStyle, setThemeStyleState] = useState<ThemeStyle>(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME_STYLE;
    return (
      resolveStoredValue<ThemeStyle>(
        THEME_STYLE_STORAGE_KEY,
        [
          'glass',
          'paper',
          'midnight',
          'midnight-contrast',
          'ocean',
          'forest',
          'morandi',
        ],
        DEFAULT_THEME_STYLE
      )
    );
  });

  const [textureMode, setTextureModeState] = useState<TextureMode>(() => {
    if (typeof window === 'undefined') return 'off';
    return resolveStoredValue<TextureMode>(TEXTURE_MODE_STORAGE_KEY, ['on', 'off'], 'off');
  });

  const [fontMode, setFontModeState] = useState<FontMode>(() => {
    if (typeof window === 'undefined') return 'sans';
    return resolveStoredValue<FontMode>(FONT_MODE_STORAGE_KEY, ['sans', 'serif'], 'sans');
  });

  useEffect(() => {
    const root = window.document.documentElement;

    const applyTheme = () => {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      const resolved = theme === 'system' ? systemTheme : theme;

      setResolvedTheme(resolved);
      root.dataset.performanceMode = resolvePerformanceMode();

      if (resolved === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    applyTheme();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const motionMediaQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
    const handler = () => applyTheme();
    mediaQuery.addEventListener('change', handler);
    motionMediaQuery.addEventListener('change', handler);

    return () => {
      mediaQuery.removeEventListener('change', handler);
      motionMediaQuery.removeEventListener('change', handler);
    };
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme-style', themeStyle);
  }, [themeStyle]);

  useEffect(() => {
    if (textureMode === 'on') {
      document.body.classList.add('paper-texture');
    } else {
      document.body.classList.remove('paper-texture');
    }
  }, [textureMode]);

  useEffect(() => {
    if (fontMode === 'serif') {
      document.body.classList.add('font-serif');
    } else {
      document.body.classList.remove('font-serif');
    }
  }, [fontMode]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  const setThemeStyle = useCallback((style: ThemeStyle) => {
    setThemeStyleState(style);
    try {
      localStorage.setItem(THEME_STYLE_STORAGE_KEY, style);
    } catch {
      // ignore
    }
  }, []);

  const setTextureMode = useCallback((mode: TextureMode) => {
    setTextureModeState(mode);
    try {
      localStorage.setItem(TEXTURE_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, []);

  const setFontMode = useCallback((mode: FontMode) => {
    setFontModeState(mode);
    try {
      localStorage.setItem(FONT_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      resolvedTheme,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
      themeStyle,
      setThemeStyle,
      textureMode,
      setTextureMode,
      fontMode,
      setFontMode,
    }),
    [resolvedTheme, setTheme, theme, toggleTheme, themeStyle, setThemeStyle, textureMode, setTextureMode, fontMode, setFontMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
